import { Gift, Loader2, X } from 'lucide-react';
import { useOfferContract } from '@/features/contracts/hooks/useContracts';
import { billedServiceHt, billedServiceTtc } from '@/features/contracts/utils/billedAmount';

interface OfferContractModalProps {
  contractId: number;
  contractLabel: string;
  /** Prestation Affinicar seule. */
  servicePrice: number | null;
  /**
   * Supplément d'assistance, s'il y en a un. DEUX colonnes distinctes en base, qui
   * s'additionnent à la lecture : l'API crédite leur somme (`billedServiceHt`), donc
   * l'annoncer sans l'assistance sous-évaluait un avoir irréversible.
   */
  assistancePrice: number | null;
  isOpen: boolean;
  onClose: () => void;
}

export function OfferContractModal({
  contractId,
  contractLabel,
  servicePrice,
  assistancePrice,
  isOpen,
  onClose,
}: OfferContractModalProps) {
  const { mutate, isLoading } = useOfferContract();

  if (!isOpen) return null;

  const htAmount = billedServiceHt(servicePrice, assistancePrice);
  const ttcAmount = billedServiceTtc(servicePrice, assistancePrice);

  const handleConfirm = () => {
    mutate(contractId, {
      onSuccess: () => onClose(),
    });
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
      {/*
        Plafond + `overflow-hidden` + colonne flex : sans eux, un panneau plus haut
        que la fenêtre est centré par le flex parent, donc son haut sort par le
        dessus — hors d'atteinte, et rien ne défile puisque l'overlay est `fixed`.
      */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-lg tw-w-full tw-max-h-[90vh] tw-overflow-hidden tw-flex tw-flex-col">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-5 tw-border-b tw-border-gray-200 tw-flex-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Gift className="tw-w-5 tw-h-5 tw-text-blue-500" />
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-m-0">
              Offrir le contrat
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="tw-p-5 tw-space-y-4 tw-flex-1 tw-overflow-y-auto">
          <p className="tw-text-sm tw-text-gray-600 tw-m-0">
            Vous allez offrir le contrat <strong>{contractLabel}</strong>.
            Un avoir sera créé pour le prescripteur.
          </p>

          <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-md tw-p-3">
            <p className="tw-text-sm tw-font-medium tw-text-blue-800 tw-m-0 tw-mb-2">
              Montants de l'avoir :
            </p>
            <ul className="tw-text-sm tw-text-blue-700 tw-m-0 tw-pl-5 tw-space-y-1">
              <li>Montant HT : <strong>{htAmount.toFixed(2)} €</strong></li>
              <li>Montant TTC : <strong>{ttcAmount.toFixed(2)} €</strong></li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-5 tw-border-t tw-border-gray-200 tw-flex-shrink-0">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-rounded-md tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          >
            {isLoading && <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />}
            Confirmer l'offre
          </button>
        </div>
      </div>
    </div>
  );
}
